import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { sendToVendorForReview, sendUpdatesToVendor, markAsInternalReview, markAsUpdatesInProgress } from '@/data/vendorReview';
import { useProductEditor } from '@/hooks/useProductEditor';
import { toast } from 'sonner';
import { Mail, Send, Clock, CheckCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

interface VendorWorkflowButtonsProps {
  productId: string;
  productSlug: string;
  status: string;
  vendorEmail?: string;
  vendorReviewToken?: string;
}

export function VendorWorkflowButtons({ 
  productId, 
  productSlug,
  status, 
  vendorEmail,
  vendorReviewToken 
}: VendorWorkflowButtonsProps) {
  const { user, isAdmin } = useAuth();
  const { updateProduct } = useProductEditor();
  const queryClient = useQueryClient();
  const [isSending, setIsSending] = useState(false);
  const [showVendorEmailDialog, setShowVendorEmailDialog] = useState(false);
  const [tempVendorEmail, setTempVendorEmail] = useState(vendorEmail || '');
  const [tempVendorName, setTempVendorName] = useState('');
  
  if (!isAdmin) return null;
  
  const getStatusBadge = () => {
    const badges: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      'draft': { label: 'Draft', variant: 'secondary' },
      'enriching': { label: 'Enriching...', variant: 'secondary' },
      'ai_generated': { label: 'AI Generated', variant: 'outline' },
      'internal_review': { label: 'Internal Review', variant: 'default' },
      'sent_to_vendor': { label: 'Sent to Vendor', variant: 'default' },
      'vendor_reviewing': { label: 'Vendor Reviewing', variant: 'default' },
      'updates_in_progress': { label: 'Updates in Progress', variant: 'secondary' },
      'awaiting_vendor': { label: 'Awaiting Vendor', variant: 'default' },
      'vendor_approved': { label: 'Vendor Approved', variant: 'default' },
      'published': { label: 'Published', variant: 'default' },
    };
    
    const badge = badges[status] || { label: status, variant: 'outline' as const };
    return <Badge variant={badge.variant}>{badge.label}</Badge>;
  };
  
  const handleMarkForReview = async () => {
    try {
      await markAsInternalReview(productId);
      toast.success('Product marked for internal review');
      queryClient.invalidateQueries({ queryKey: ['product', productSlug] });
    } catch (error: any) {
      toast.error('Failed: ' + error.message);
    }
  };
  
  const handleSendToVendor = async () => {
    if (!tempVendorEmail) {
      toast.error('Please set vendor email first');
      return;
    }
    
    setIsSending(true);
    try {
      // First save vendor email if changed via API
      if (tempVendorEmail !== vendorEmail) {
        await fetch('/api/update-vendor', {
          method: 'POST',
          body: JSON.stringify({ productId, vendorEmail: tempVendorEmail, vendorName: tempVendorName })
        });
      }
      
      await sendToVendorForReview(productId);
      toast.success(`Review email sent to ${tempVendorEmail}`);
      setShowVendorEmailDialog(false);
      queryClient.invalidateQueries({ queryKey: ['product', productSlug] });
    } catch (error: any) {
      toast.error('Failed to send: ' + error.message);
    } finally {
      setIsSending(false);
    }
  };
  
  const handleSendUpdates = async () => {
    setIsSending(true);
    try {
      await sendUpdatesToVendor(productId);
      toast.success('Update notification sent to vendor');
      queryClient.invalidateQueries({ queryKey: ['product', productSlug] });
    } catch (error: any) {
      toast.error('Failed to send updates: ' + error.message);
    } finally {
      setIsSending(false);
    }
  };
  
  const handleMarkUpdatesInProgress = async () => {
    try {
      await markAsUpdatesInProgress(productId);
      toast.success('Product marked as updates in progress');
      queryClient.invalidateQueries({ queryKey: ['product', productSlug] });
    } catch (error: any) {
      toast.error('Failed: ' + error.message);
    }
  };
  
  return (
    <>
      <div className="fixed bottom-6 right-6 z-50 bg-card border rounded-lg shadow-lg p-4 space-y-3 max-w-sm">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm">Vendor Workflow</h4>
          {getStatusBadge()}
        </div>
        
        {vendorReviewToken && (
          <div className="text-xs text-muted-foreground">
            <a 
              href={`/vendor-review/${vendorReviewToken}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-primary hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              View Vendor Page
            </a>
          </div>
        )}
        
        {/* AI Generated → Mark for Internal Review */}
        {status === 'ai_generated' && (
          <Button
            size="sm"
            onClick={handleMarkForReview}
            className="w-full"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Mark for Internal Review
          </Button>
        )}
        
        {/* Internal Review → Send to Vendor */}
        {(status === 'internal_review' || status === 'ai_generated') && (
          <Button
            size="sm"
            onClick={() => {
              setTempVendorEmail(vendorEmail || '');
              setShowVendorEmailDialog(true);
            }}
            disabled={isSending}
            className="w-full"
          >
            <Mail className="w-4 h-4 mr-2" />
            {isSending ? 'Sending...' : 'Send to Vendor for Review'}
          </Button>
        )}
        
        {/* Vendor Reviewing → Mark Updates in Progress */}
        {status === 'vendor_reviewing' && (
          <Button
            size="sm"
            onClick={handleMarkUpdatesInProgress}
            variant="outline"
            className="w-full"
          >
            <Clock className="w-4 h-4 mr-2" />
            Mark Updates in Progress
          </Button>
        )}
        
        {/* Updates in Progress → Send Updates to Vendor */}
        {status === 'updates_in_progress' && (
          <Button
            size="sm"
            onClick={handleSendUpdates}
            disabled={isSending}
            className="w-full"
          >
            <Send className="w-4 h-4 mr-2" />
            {isSending ? 'Sending...' : 'Send Updates to Vendor'}
          </Button>
        )}
      </div>
      
      {/* Vendor Email Dialog */}
      <Dialog open={showVendorEmailDialog} onOpenChange={setShowVendorEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send to Vendor for Review</DialogTitle>
            <DialogDescription>
              Enter the vendor's contact information to send them a review link.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="vendorEmail">Vendor Email *</Label>
              <Input
                id="vendorEmail"
                type="email"
                value={tempVendorEmail}
                onChange={(e) => setTempVendorEmail(e.target.value)}
                placeholder="vendor@company.com"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="vendorName">Vendor Name (optional)</Label>
              <Input
                id="vendorName"
                value={tempVendorName}
                onChange={(e) => setTempVendorName(e.target.value)}
                placeholder="John Doe"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendToVendor} 
              disabled={!tempVendorEmail || isSending}
            >
              {isSending ? 'Sending...' : 'Send Review Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
